'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { RiCloseFill } from 'react-icons/ri';

const CustomDialog = DialogPrimitive.Root;

const CustomDialogTrigger = DialogPrimitive.Trigger;

const CustomDialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
CustomDialogPortal.displayName = DialogPrimitive.Portal.displayName;

const CustomDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
CustomDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  {
    showCustomClose?: boolean;
    showImageClose?: boolean;
  } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, showCustomClose, showImageClose, ...props }, ref) => (
  <CustomDialogPortal>
    <CustomDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed bottom-0 left-[50%] z-50 grid h-[95vh] w-full  translate-x-[-50%] gap-4 border bg-background px-0 pt-8 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full md:px-16',
        className,
      )}
      {...props}
    >
      <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>
      {showCustomClose && (
        <div className="flex items-center justify-between px-4 md:px-0">
          <DialogPrimitive.Close className="flex items-center justify-end gap-2.5 text-accent disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <RiCloseFill size={20} />
            <span className="hidden text-accent md:block">Zavrieť</span>
          </DialogPrimitive.Close>
        </div>
      )}
      {showImageClose && (
        <DialogPrimitive.Close className="fixed right-[7%] top-[7%] z-[52] rounded-full bg-background p-2.5 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground sm:right-[10%] md:top-[10%]">
          <RiCloseFill size={20} />
        </DialogPrimitive.Close>
      )}

      {children}
    </DialogPrimitive.Content>
  </CustomDialogPortal>
));
CustomDialogContent.displayName = DialogPrimitive.Content.displayName;

const CustomDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
CustomDialogHeader.displayName = 'DialogHeader';

const CustomDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
CustomDialogFooter.displayName = 'DialogFooter';

const CustomDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
CustomDialogTitle.displayName = DialogPrimitive.Title.displayName;

const CustomDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CustomDialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  CustomDialog,
  CustomDialogContent,
  CustomDialogDescription,
  CustomDialogFooter,
  CustomDialogHeader,
  CustomDialogTitle,
  CustomDialogTrigger,
};
